import { useCallback } from "react";
import moment from "moment";
import type { IAttendanceDetail } from "@/interface/attendance.interface";

export type AttendanceStatus =
  | "ABSENT"
  | "PRESENT"
  | "WO"
  | "HOLIDAY"
  | "HALF_DAY"
  | "LEAVE"
  | "WFH"
  | "OUT_DUTY"
  | "F-D WFH"
  | "F-D OD"
  | "WO+P"
  | "HOLIDAY+P"
  | "WFH+P"
  | "P+WFH"
  | "OD+P"
  | "P+OD"
  | "L+P"
  | "P+L"
  | "L+WFH"
  | "WFH+L"
  | "L+OD"
  | "OD+L"
  | "WFH+OD"
  | "WFH+OD+P"
  | "P+MISSED"
  | "WO+HD"
  | "HOLIDAY+HD"
  | "--";

export interface DayColumnInfo {
  dayStr: string; // e.g. "01", "02"
  fullDate: string; // e.g. "2026-08-02"
  dayName: string; // e.g. "Sun", "Mon"
  isSunday: boolean;
}

export interface AttendanceRuleContext {
  fullDate: string;
  currentStatus?: AttendanceStatus;
  detail?: IAttendanceDetail | null;
}

/**
 * Parsed Context Data Structure for Attendance Evaluation
 */
export interface ParsedDayContext {
  fullDate: string;
  isSunday: boolean;
  isHoliday: boolean;
  isLeave: boolean;
  isHalfDay: boolean;
  isAutoMarkedAbsent: boolean;
  wfhDuration: "FULL_DAY" | "FIRST_HALF" | "SECOND_HALF" | null;
  odDuration: "FULL_DAY" | "FIRST_HALF" | "SECOND_HALF" | null;
  leaveDuration: "FULL_DAY" | "FIRST_HALF" | "SECOND_HALF" | null;
  hasFirstHalfPunch: boolean;
  hasSecondHalfPunch: boolean;
  hasAnyPunch: boolean;
  isFullDayComplete: boolean;
  currentStatus?: AttendanceStatus;
}

/**
 * Helper: Safely parses raw attendance detail object into structured flags.
 * Handles Array and Object formats for WFH, OD, and Leave requests gracefully.
 */
export const parseAttendanceContext = (
  fullDate: string,
  currentStatus?: AttendanceStatus,
  detail?: IAttendanceDetail | null,
): ParsedDayContext => {
  const dateObj = moment(fullDate, "YYYY-MM-DD");
  const isSunday = dateObj.isValid() && dateObj.day() === 0;
  const isHoliday = detail?.status === "HOLIDAY" || currentStatus === "HOLIDAY";
  const isLeave = Boolean(detail?.leaves) || currentStatus === "LEAVE";
  const isHalfDay =
    detail?.status === "HALF_DAY" || currentStatus === "HALF_DAY";

  // Safe extraction for WFH (supports Array or Object)
  const wfhObj = Array.isArray(detail?.wfhRequests)
    ? detail.wfhRequests[0]
    : detail?.wfhRequests;
  const wfhDuration = wfhObj?.duration || (wfhObj ? "FULL_DAY" : null);

  // Safe extraction for OD (supports Array or Object)
  const odObj = Array.isArray(detail?.outdoorDuties)
    ? detail.outdoorDuties[0]
    : detail?.outdoorDuties;
  const odDuration = odObj?.duration || (odObj ? "FULL_DAY" : null);

  // Safe extraction for Leaves (supports Array or Object)
  const leaveObj = Array.isArray(detail?.leaves)
    ? detail.leaves[0]
    : detail?.leaves;
  const leaveDuration =
    leaveObj?.duration || (leaveObj || isLeave ? "FULL_DAY" : null);

  // Presence & punch flags
  const fhIn = Boolean(detail?.firstHalfCheckInTime);
  const fhOut = Boolean(detail?.firstHalfCheckOutTime);
  const shIn = Boolean(detail?.secondHalfCheckInTime);
  const shOut = Boolean(detail?.secondHalfCheckOutTime);
  const genIn = Boolean(detail?.checkInTime);
  const genOut = Boolean(detail?.checkOutTime);

  const hasFirstHalfPunch = fhIn || fhOut;
  const hasSecondHalfPunch = shIn || shOut;
  const hasAnyPunch =
    hasFirstHalfPunch || hasSecondHalfPunch || genIn || genOut;

  const isFullFirstHalf = fhIn && fhOut;
  const isFullSecondHalf = shIn && shOut;
  const isFullDayComplete =
    (isFullFirstHalf && isFullSecondHalf) || (genIn && genOut);

  // Check if system auto-marked as ABSENT due to missing punches
  const isAutoMarkedAbsent =
    (detail?.status === "ABSENT" || currentStatus === "ABSENT") &&
    !isFullDayComplete &&
    !isSunday &&
    !isHoliday;

  return {
    fullDate,
    isSunday,
    isHoliday,
    isLeave,
    isHalfDay,
    isAutoMarkedAbsent,
    wfhDuration,
    odDuration,
    leaveDuration,
    hasFirstHalfPunch,
    hasSecondHalfPunch,
    hasAnyPunch,
    isFullDayComplete,
    currentStatus,
  };
};

/**
 * Core Decision Engine: Linear, Priority-Based Status Calculator.
 * Prevents rule collisions and provides predictable, unambiguous resolution.
 */
export const calculateStatusFromContext = (
  parsed: ParsedDayContext,
): AttendanceStatus => {
  // -------------------------------------------------------------
  // Priority 1: Full-Day Leave
  // -------------------------------------------------------------
  if (parsed.leaveDuration === "FULL_DAY") {
    return "LEAVE";
  }

  // -------------------------------------------------------------
  // Priority 2: Full-Day WFH / OD Requests (F-D ONLY if isFullDayComplete)
  // -------------------------------------------------------------
  if (parsed.wfhDuration === "FULL_DAY") {
    return parsed.isFullDayComplete ? "F-D WFH" : "WFH";
  }
  if (parsed.odDuration === "FULL_DAY") {
    return parsed.isFullDayComplete ? "F-D OD" : "OUT_DUTY";
  }

  // -------------------------------------------------------------
  // Priority 3: Multi-Half Combinations (WFH, OD, Leave, Punch)
  // -------------------------------------------------------------
  // WFH & OD Combinations
  if (
    parsed.wfhDuration === "FIRST_HALF" &&
    parsed.odDuration === "SECOND_HALF"
  ) {
    return parsed.hasAnyPunch ? "WFH+OD+P" : "WFH+OD";
  }
  if (
    parsed.wfhDuration === "FIRST_HALF" &&
    (parsed.hasSecondHalfPunch || parsed.hasAnyPunch)
  ) {
    return "WFH+P";
  }
  if (parsed.hasFirstHalfPunch && parsed.wfhDuration === "SECOND_HALF") {
    return "P+WFH";
  }
  if (
    parsed.odDuration === "FIRST_HALF" &&
    (parsed.hasSecondHalfPunch || parsed.hasAnyPunch)
  ) {
    return "OD+P";
  }
  if (parsed.hasFirstHalfPunch && parsed.odDuration === "SECOND_HALF") {
    return "P+OD";
  }

  // Leave Combinations (1st Half Leave or 2nd Half Leave)
  if (parsed.leaveDuration === "FIRST_HALF") {
    if (parsed.wfhDuration === "SECOND_HALF") return "L+WFH";
    if (parsed.odDuration === "SECOND_HALF") return "L+OD";
    if (parsed.hasSecondHalfPunch || parsed.hasAnyPunch) return "L+P";
  }
  if (parsed.leaveDuration === "SECOND_HALF") {
    if (parsed.wfhDuration === "FIRST_HALF") return "WFH+L";
    if (parsed.odDuration === "FIRST_HALF") return "OD+L";
    if (parsed.hasFirstHalfPunch || parsed.hasAnyPunch) return "P+L";
  }

  // -------------------------------------------------------------
  // Priority 4: Week-Off (Sunday) Conditions
  // -------------------------------------------------------------
  if (parsed.isSunday) {
    if (parsed.hasAnyPunch || parsed.isHalfDay) {
      return parsed.isHalfDay ? "WO+HD" : "WO+P";
    }
    return "WO";
  }

  // -------------------------------------------------------------
  // Priority 5: Holiday Conditions
  // -------------------------------------------------------------
  if (parsed.isHoliday) {
    if (parsed.hasAnyPunch || parsed.isHalfDay) {
      return parsed.isHalfDay ? "HOLIDAY+HD" : "HOLIDAY+P";
    }
    return "HOLIDAY";
  }

  // -------------------------------------------------------------
  // Priority 6: Normal Working Day Attendance Logic (Mon - Sat)
  // -------------------------------------------------------------
  if (parsed.isFullDayComplete) {
    return "PRESENT";
  }

  if (parsed.hasAnyPunch && !parsed.isFullDayComplete && !parsed.isHalfDay) {
    return "P+MISSED";
  }

  if (parsed.isHalfDay) {
    return "HALF_DAY";
  }

  if (!parsed.hasAnyPunch) {
    if (
      parsed.currentStatus === "ABSENT" ||
      !parsed.currentStatus ||
      parsed.currentStatus === "--"
    ) {
      return "ABSENT";
    }
  }

  return parsed.currentStatus || "--";
};

/**
 * Custom Hook for managing date-wise attendance rules & status calculations.
 */
export const useAttendanceRules = () => {
  /**
   * Check if a given date string (YYYY-MM-DD) is a Sunday.
   */
  const checkIsSunday = useCallback((fullDate: string): boolean => {
    if (!fullDate) return false;
    const dateObj = moment(fullDate, "YYYY-MM-DD");
    return dateObj.isValid() && dateObj.day() === 0;
  }, []);

  /**
   * Evaluates attendance status using the structured decision engine.
   * ONLY calculates status if an attendance record exists (detail is present). Otherwise returns '--'.
   */
  const getCalculatedStatus = useCallback(
    (
      fullDate: string,
      currentStatus?: AttendanceStatus,
      detail?: IAttendanceDetail | null,
    ): AttendanceStatus => {
      // Respect manual user selection if user manually set a status other than '--'
      if (currentStatus && currentStatus !== "--" && !detail) {
        return currentStatus;
      }

      // Only run rule calculations if attendance data actually exists for this date from the API
      if (!detail) {
        return "--";
      }

      const parsedContext = parseAttendanceContext(
        fullDate,
        currentStatus,
        detail,
      );
      return calculateStatusFromContext(parsedContext);
    },
    [],
  );

  /**
   * Generates detailed day column objects between startDate and endDate.
   */
  const getDayColumns = useCallback(
    (startDate?: string, endDate?: string): DayColumnInfo[] => {
      if (!startDate || !endDate) return [];
      const start = moment(startDate, "YYYY-MM-DD");
      const end = moment(endDate, "YYYY-MM-DD");
      const days: DayColumnInfo[] = [];

      if (start.isValid() && end.isValid() && end.isSameOrAfter(start)) {
        const current = start.clone();
        while (current.isSameOrBefore(end)) {
          const fullDate = current.format("YYYY-MM-DD");
          const dayStr = current.format("DD");
          const dayName = current.format("ddd");
          const isSunday = current.day() === 0;

          days.push({
            dayStr,
            fullDate,
            dayName,
            isSunday,
          });
          current.add(1, "day");
        }
      }
      return days;
    },
    [],
  );

  /**
   * Applies attendance rules across an entire employee attendance map for all day columns.
   */
  const applyRulesToAttendanceMap = useCallback(
    (
      attendanceMap: Record<string, AttendanceStatus>,
      dayColumns: DayColumnInfo[],
      detailMap?: Record<string, IAttendanceDetail>,
    ): Record<string, AttendanceStatus> => {
      const updatedMap: Record<string, AttendanceStatus> = { ...attendanceMap };

      dayColumns.forEach((col) => {
        const existingStatus = updatedMap[col.dayStr];
        const detail = detailMap ? detailMap[col.dayStr] : null;
        const calculated = getCalculatedStatus(
          col.fullDate,
          existingStatus,
          detail,
        );
        updatedMap[col.dayStr] = calculated;
      });

      return updatedMap;
    },
    [getCalculatedStatus],
  );

  return {
    checkIsSunday,
    getCalculatedStatus,
    getDayColumns,
    applyRulesToAttendanceMap,
  };
};
